function nestMenuElementsByHierarchy(
  drupalMenuElements: NerdMenuDrupalElement[],
): NerdMenuElement[] {
  const menuElementByHierarchyKey = new Map<string, NerdMenuElement>();
  const nerdMenuElementsNested: NerdMenuElement[] = [];

  // Sort the input elements by hierarchy length and then by hierarchy values.
  // This ensures that parent elements are processed and added to the map before their children.
  const sortedDrupalMenuElements = [...drupalMenuElements].sort((a, b) => {
    // Primary sort: by hierarchy length (e.g., [0] comes before [0,0])
    if (a.hierarchy.length !== b.hierarchy.length) {
      return a.hierarchy.length - b.hierarchy.length;
    }

    // Secondary sort: by hierarchy values if lengths are equal (e.g., [0,0] before [0,1])
    for (let i = 0; i < a.hierarchy.length; i++) {
      if (a.hierarchy[i] !== b.hierarchy[i]) {
        return a.hierarchy[i] - b.hierarchy[i];
      }
    }

    return 0;
  });

  sortedDrupalMenuElements.forEach(drupalElement => {
    const newMenuElement: NerdMenuElement = {
      title: drupalElement.title,
      link: drupalElement.link,
      type: drupalElement.field_menu_link_type,
      level: drupalElement.hierarchy.length - 1, // 0 for top-level, 1 for first child, etc.
    };

    // Bind the component data if it exists
    if (drupalElement.relationships && drupalElement.relationships.field_storage) {
      newMenuElement.component = drupalElement.relationships.field_storage;
    }

    const currentHierarchy = drupalElement.hierarchy;
    const hierarchyKey = currentHierarchy.join(','); // e.g., [0,0] becomes "0,0"

    menuElementByHierarchyKey.set(hierarchyKey, newMenuElement);

    if (currentHierarchy.length === 1) {
      nerdMenuElementsNested.push(newMenuElement);
      return;
    }

    const parentHierarchy = currentHierarchy.slice(0, -1);
    const parentHierarchyKey = parentHierarchy.join(',');
    const parentMenuElement = menuElementByHierarchyKey.get(parentHierarchyKey);

    if (!parentMenuElement) {
      // eslint-disable-next-line no-console
      console.error(`Orphaned element found: ${drupalElement.title} (Hierarchy: ${hierarchyKey}).`);
      return;
    }

    if (!parentMenuElement.menuElements) {
      parentMenuElement.menuElements = [];
    }

    parentMenuElement.menuElements.push(newMenuElement);
  });

  return nerdMenuElementsNested;
}

/**
 * Converts a NerdMenuDrupalEdges object to a NerdMenu object for use in the HeaderWithNerdMenu component
 * @param nerdMenuDrupalEdges The Drupal menu edges data structure
 * @returns A NerdMenu object that can be used by the HeaderWithNerdMenu component
 */
export function convertToNerdMenu(nerdMenuDrupalEdges: NerdMenuDrupalEdges): NerdMenu {
  if (!nerdMenuDrupalEdges?.edges?.length || !nerdMenuDrupalEdges.edges[0]?.node) {
    // eslint-disable-next-line no-console
    console.error('Not enough menus provided');

    return {
      menu_id: 'no-menu',
      menuElements: [],
    };
  }

  if (nerdMenuDrupalEdges.edges.length > 1) {
    // eslint-disable-next-line no-console
    console.error('Too many menus provided');

    return {
      menu_id: 'too-many-menus',
      menuElements: [],
    };
  }

  // Extract the menu from the first edge node
  const nerdMenuDrupal = nerdMenuDrupalEdges.edges[0].node;

  return {
    menu_id: nerdMenuDrupal.menu_id,
    menuElements: nestMenuElementsByHierarchy(nerdMenuDrupal.relationships.field_links),
  };
}
